<template>
  <div>
    <!-- Main Install Banner (matches Chrome banner appearance) -->
    <transition name="slide-up">
      <div 
        v-if="showInstallPrompt && !isInstalled" 
        class="fixed bottom-4 left-4 right-4 md:left-1/2 md:right-auto md:transform md:-translate-x-1/2 z-50 max-w-md w-auto md:w-full"
      >
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-3 md:gap-4">
          <div class="flex-shrink-0">
            <img 
              :src="`${formattedPublicPath}icons/web-app-manifest-192x192.png`"
              alt="App Icon" 
              class="w-10 h-10 md:w-12 md:h-12 rounded-lg object-contain"
            />
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
              Install App
            </h3>
            <p class="text-xs text-gray-600 dark:text-gray-400 mt-0.5 hidden sm:block">
              Install this app on your device for quick and easy access
            </p>
          </div>
          <div class="flex gap-2 flex-shrink-0">
            <button
              @click="installPWA"
              class="px-3 py-2 md:px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors shadow-sm"
            >
              Install
            </button>
            <button
              @click="dismissInstallPrompt"
              class="px-2 py-2 md:px-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm transition-colors"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- iOS / Safari Step-by-Step Installation Modal -->
    <transition name="fade">
      <div 
        v-if="showIOSInstructions" 
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-3 sm:p-4"
        @click.self="closeIOSInstructions"
      >
        <div 
          class="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl sm:rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-5 space-y-4 max-h-[90vh] overflow-y-auto animate-modal"
        >
          <!-- Header -->
          <div class="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-3">
              <img 
                :src="`${formattedPublicPath}icons/web-app-manifest-192x192.png`"
                alt="Jeducare Logo" 
                class="w-9 h-9 rounded-lg object-contain bg-gray-50 dark:bg-gray-700 p-0.5"
              />
              <div>
                <h3 class="text-base font-semibold text-gray-900 dark:text-white">
                  Install Jeducare App
                </h3>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ isMacSafari ? 'Safari for Mac' : 'Safari for iOS / Apple' }}
                </p>
              </div>
            </div>
            <button
              @click="closeIOSInstructions"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-lg transition-colors"
              aria-label="Close"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- iOS Instructions (iPhone / iPad) -->
          <div v-if="!isMacSafari" class="space-y-3.5 text-sm">
            <p class="text-xs text-gray-600 dark:text-gray-300">
              Apple Safari does not allow automatic installs, but you can add Jeducare to your home screen in 3 quick steps:
            </p>

            <div class="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-750 rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-lg flex-shrink-0">
                <!-- Apple Share Icon -->
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
              </div>
              <div class="min-w-0">
                <span class="font-semibold text-gray-900 dark:text-white">Step 1:</span>
                <p class="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                  Tap the <strong>Share</strong> button in Safari's toolbar (at the bottom on iPhone, or top on iPad).
                </p>
              </div>
            </div>

            <div class="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-750 rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-lg flex-shrink-0">
                <!-- Add to Home Screen Icon -->
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="4" ry="4" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              </div>
              <div class="min-w-0">
                <span class="font-semibold text-gray-900 dark:text-white">Step 2:</span>
                <p class="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                  Scroll down the share sheet and tap <strong>"Add to Home Screen"</strong>.
                </p>
              </div>
            </div>

            <div class="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-750 rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-lg flex-shrink-0">
                <!-- Check Icon -->
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div class="min-w-0">
                <span class="font-semibold text-gray-900 dark:text-white">Step 3:</span>
                <p class="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                  Tap <strong>"Add"</strong> in the top-right corner to complete installation.
                </p>
              </div>
            </div>
          </div>

          <!-- macOS Safari Instructions -->
          <div v-else class="space-y-3.5 text-sm">
            <p class="text-xs text-gray-600 dark:text-gray-300">
              To install Jeducare on macOS using Safari:
            </p>

            <div class="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-750 rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-lg flex-shrink-0">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div class="min-w-0">
                <span class="font-semibold text-gray-900 dark:text-white">Step 1:</span>
                <p class="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                  Click <strong>File</strong> in Safari's top menu bar.
                </p>
              </div>
            </div>

            <div class="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-750 rounded-xl border border-gray-100 dark:border-gray-700">
              <div class="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-lg flex-shrink-0">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
              <div class="min-w-0">
                <span class="font-semibold text-gray-900 dark:text-white">Step 2:</span>
                <p class="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                  Select <strong>"Add to Dock..."</strong> and click <strong>Add</strong>.
                </p>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="pt-2 flex flex-col sm:flex-row gap-2">
            <button
              @click="closeIOSInstructions"
              class="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
            >
              Got it
            </button>
            <button
              @click="dismissInstallPrompt"
              class="w-full sm:w-auto py-2.5 px-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xs transition-colors"
            >
              Don't show again
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { usePWAInstall } from '@/composables/usePWAInstall'

const {
  showInstallPrompt,
  isInstalled,
  isMacSafari,
  showIOSInstructions,
  installPWA,
  closeIOSInstructions,
  dismissInstallPrompt
} = usePWAInstall()

const publicPath = process.env.BASE_URL || '/portal/'
const formattedPublicPath = computed(() => {
  return publicPath.endsWith('/') ? publicPath : `${publicPath}/`
})
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease-out;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translate(-50%, 100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes modalPop {
  0% {
    transform: scale(0.95) translateY(10px);
    opacity: 0;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

.animate-modal {
  animation: modalPop 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>
